import { useMutation } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { track } from '@core/logging';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo, useCallback } from 'react';

import { type SkipAuditItemResponse, type SkipAuditItemVariables, type UnskipAuditItemResponse, type UnskipAuditItemVariables } from '../domain/graphql-types';
import { SKIP_AUDIT_ITEM, UNSKIP_AUDIT_ITEM } from '../graphql/mutations/skipAuditItem';
import { updateAuditItemAndStats } from '../utils/auditCacheUpdates';

/**
 * Hook for skipping/unskipping audit items with optimistic updates
 * Enhanced with adapter pattern for better error handling and separation of concerns
 * Includes strategic business event tracking for observability compliance
 */
export function useSkipAuditItem() {
  const [skipAuditItemMutation, { data: skipData, loading: skipLoading, error: skipError }] = useMutation<SkipAuditItemResponse, SkipAuditItemVariables>(
    SKIP_AUDIT_ITEM,
    {
      errorPolicy: 'all',
      context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
      update: (cache, { data: mutationData }) => {
        // Only update cache if the mutation was successful
        if (mutationData?.skipAuditItem) {
          const { item } = mutationData.skipAuditItem;

          // Update audit item and invalidate stats for reactive progress updates
          updateAuditItemAndStats(cache, item);
        }
      },
    }
  );

  const [unskipAuditItemMutation, { data: unskipData, loading: unskipLoading, error: unskipError }] = useMutation<
    UnskipAuditItemResponse,
    UnskipAuditItemVariables
  >(UNSKIP_AUDIT_ITEM, {
    errorPolicy: 'all',
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
    update: (cache, { data: mutationData }) => {
      // Only update cache if the mutation was successful
      if (mutationData?.unskipAuditItem) {
        const { item } = mutationData.unskipAuditItem;

        // Update audit item and invalidate stats for reactive progress updates
        updateAuditItemAndStats(cache, item);
      }
    },
  });

  // Use GraphQL data directly for skip
  const updatedSkippedItem = useMemo(() => {
    if (!skipData?.skipAuditItem) {
      return undefined;
    }

    return skipData.skipAuditItem.item;
  }, [skipData]);

  // Use GraphQL data directly for unskip
  const updatedUnskippedItem = useMemo(() => {
    if (!unskipData?.unskipAuditItem) {
      return undefined;
    }

    return unskipData.unskipAuditItem.item;
  }, [unskipData]);

  // Transform errors using simplified adapter pattern - return arrays for consistency
  const skipDomainErrors = useMemo(() => {
    if (!skipError) return [];

    // Map GraphQL errors to user-friendly format
    const mappedError = transformError(skipError);

    // Return error array for UI handling
    return [mappedError];
  }, [skipError]);

  const unskipDomainErrors = useMemo(() => {
    if (!unskipError) return [];

    // Map GraphQL errors to user-friendly format
    const mappedError = transformError(unskipError);

    // Return error array for UI handling
    return [mappedError];
  }, [unskipError]);

  const skipAuditItem = useCallback(
    async (variables: SkipAuditItemVariables) => {
      const { input } = variables;

      // Track audit item skip initiation
      track('SkipAuditItemInitiated', {
        itemId: input.itemId,
      });

      try {
        // Skip optimistic response to avoid cache invalidation issues on failure
        // We handle optimistic updates at the component level instead
        const result = await skipAuditItemMutation({
          variables,
          update: (cache, { data: mutationData }) => {
            // Only run this update for successful mutations
            if (mutationData?.skipAuditItem) {
              const { item } = mutationData.skipAuditItem;

              // Update audit item and invalidate stats for reactive progress updates
              updateAuditItemAndStats(cache, item);
            }
          },
        });

        // Check for success: data exists and no errors
        if (result.data?.skipAuditItem && !result.errors) {
          const itemData = result.data.skipAuditItem.item;

          // Track successful audit item skip
          track('SkipAuditItemSucceeded', {
            itemId: input.itemId,
            auditSessionId: itemData.auditSessionId,
          });

          return {
            success: true,
            data: itemData,
          };
        }

        // Handle errors - normalize to always use errors array (matches delete pattern)
        const errors = result.errors || [];
        const mappedErrors = errors.map(transformError);
        const firstError = mappedErrors[0];

        // Track audit item skip failure
        track('SkipAuditItemFailed', {
          itemId: input.itemId,
          errorType: 'api_error',
          errorMessage: firstError?.message || 'Unknown error',
        });

        return {
          success: false,
          errors: mappedErrors,
        };
      } catch (error) {
        // Track unexpected audit item skip failure
        track('SkipAuditItemFailed', {
          itemId: input.itemId,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [skipAuditItemMutation]
  );

  const unskipAuditItem = useCallback(
    async (variables: UnskipAuditItemVariables) => {
      const { input } = variables;

      // Track audit item unskip initiation
      track('UnskipAuditItemInitiated', {
        itemId: input.itemId,
      });

      try {
        // Skip optimistic response to avoid cache invalidation issues on failure
        // We handle optimistic updates at the component level instead
        const result = await unskipAuditItemMutation({
          variables,
          update: (cache, { data: mutationData }) => {
            // Only run this update for successful mutations
            if (mutationData?.unskipAuditItem) {
              const { item } = mutationData.unskipAuditItem;

              // Update audit item and invalidate stats for reactive progress updates
              updateAuditItemAndStats(cache, item);
            }
          },
        });

        // Check for success: data exists and no errors
        if (result.data?.unskipAuditItem && !result.errors) {
          const itemData = result.data.unskipAuditItem.item;

          // Track successful audit item unskip
          track('UnskipAuditItemSucceeded', {
            itemId: input.itemId,
            auditSessionId: itemData.auditSessionId,
          });

          return {
            success: true,
            data: itemData,
          };
        }

        // Handle errors - normalize to always use errors array (matches delete pattern)
        const errors = result.errors || [];
        const mappedErrors = errors.map(transformError);
        const firstError = mappedErrors[0];

        // Track audit item unskip failure
        track('UnskipAuditItemFailed', {
          itemId: input.itemId,
          errorType: 'api_error',
          errorMessage: firstError?.message || 'Unknown error',
        });

        return {
          success: false,
          errors: mappedErrors,
        };
      } catch (error) {
        // Track unexpected audit item unskip failure
        track('UnskipAuditItemFailed', {
          itemId: input.itemId,
          errorType: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : 'Unexpected error',
        });

        throw error;
      }
    },
    [unskipAuditItemMutation]
  );

  return {
    skipAuditItem,
    unskipAuditItem,
    updatedSkippedItem,
    updatedUnskippedItem,
    loading: skipLoading || unskipLoading,
    errors: [...skipDomainErrors, ...unskipDomainErrors], // Combined error arrays for hook state
  };
}
